import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations, LANGS } from "@/i18n/translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "eclat_lang";

function deepMerge(base, override) {
  if (!override) return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  for (const k of Object.keys(override)) {
    const ov = override[k];
    if (ov && typeof ov === "object" && !Array.isArray(ov) && base[k] && typeof base[k] === "object") {
      out[k] = deepMerge(base[k], ov);
    } else if (ov !== undefined && ov !== null && ov !== "") {
      out[k] = ov;
    }
  }
  return out;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && ["fr", "he", "en"].includes(saved) ? saved : "fr";
  });
  const [overrides, setOverrides] = useState({});

  const applyDir = useCallback((l) => {
    document.documentElement.dir = l === "he" ? "rtl" : "ltr";
    document.documentElement.lang = l;
  }, []);

  useEffect(() => {
    applyDir(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang, applyDir]);

  const setLang = useCallback((l) => setLangState(l), []);

  const t = useMemo(
    () => deepMerge(translations[lang], overrides[lang] || {}),
    [lang, overrides]
  );

  const value = {
    lang,
    setLang,
    dir: lang === "he" ? "rtl" : "ltr",
    isRTL: lang === "he",
    t,
    langs: LANGS,
    setOverrides,
  };
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
